import { useState } from 'react'
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/useAuthStore'
import BonUrbaLogo from '../assets/icons/BonUrbaLogo'

const NAV_ITEMS = [
  {
    path: '/app',
    label: 'Carte',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="m3 7 6-3 6 3 6-3v13l-6 3-6-3-6 3Z" />
        <path d="M9 4v13M15 7v13" />
      </svg>
    ),
  },
  {
    path: '/account',
    label: 'Mon compte',
    exact: true,
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </svg>
    ),
  },
]

export default function UserLayout() {
  const [collapsed, setCollapsed] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout } = useAuthStore()

  const initials = user?.first_name && user?.last_name
    ? `${user.first_name[0]}${user.last_name[0]}`.toUpperCase()
    : user?.email?.[0]?.toUpperCase() || '?'

  const handleLogout = () => {
    logout()
    navigate('/auth')
  }

  const isActive = (item) => {
    if (item.exact) return location.pathname === item.path
    return location.pathname.startsWith(item.path)
  }

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <aside
        className={`flex flex-col bg-white border-r border-gray-200 shadow-sm transition-all duration-200 ${
          collapsed ? 'w-16' : 'w-60'
        }`}
      >
        {/* Header */}
        <div className="flex items-center h-14 border-b border-gray-100 px-3 gap-2">
          {collapsed ? (
            <button
              onClick={() => setCollapsed(false)}
              className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors mx-auto"
              title="Ouvrir le menu"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600">
                <path d="M3 12h18M3 6h18M3 18h18" />
              </svg>
            </button>
          ) : (
            <>
              <Link to="/" className="flex-1 flex items-center no-underline">
                <BonUrbaLogo height={36} />
              </Link>
              <button
                onClick={() => setCollapsed(true)}
                className="p-1 rounded-lg hover:bg-gray-100 transition-colors"
                title="Reduire le menu"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400">
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>
            </>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-2 px-2 flex flex-col gap-1">
          {NAV_ITEMS.map((item) => {
            const active = isActive(item)
            return (
              <Link
                key={item.path}
                to={item.path}
                title={collapsed ? item.label : undefined}
                className={`flex items-center gap-3 px-2.5 py-2.5 rounded-lg no-underline transition-colors ${
                  active
                    ? 'bg-green-50 text-green-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                }`}
              >
                <span className="flex-shrink-0">{item.icon}</span>
                {!collapsed && <span className="text-sm font-medium">{item.label}</span>}
              </Link>
            )
          })}

          {/* Lien admin conditionnel */}
          {user?.role === 'admin' && (
            <>
              <div className="my-1 border-t border-gray-100" />
              <Link
                to="/admin"
                title={collapsed ? 'Administration' : undefined}
                className={`flex items-center gap-3 px-2.5 py-2.5 rounded-lg no-underline transition-colors ${
                  location.pathname.startsWith('/admin')
                    ? 'bg-green-50 text-green-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                }`}
              >
                <span className="flex-shrink-0">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </span>
                {!collapsed && <span className="text-sm font-medium">Admin</span>}
              </Link>
            </>
          )}
        </nav>

        {/* Footer : utilisateur + deconnexion */}
        <div className="border-t border-gray-100 px-2 py-2 flex flex-col gap-1">
          {collapsed ? (
            <>
              <div className="flex items-center justify-center w-full p-2.5">
                <div className="w-7 h-7 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-xs font-bold">
                  {initials}
                </div>
              </div>
              <button
                onClick={handleLogout}
                title="Se deconnecter"
                className="flex items-center justify-center w-full p-2.5 rounded-lg text-gray-500 hover:bg-red-50 hover:text-red-600 transition-colors"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
                </svg>
              </button>
            </>
          ) : (
            <div className="flex items-center gap-2 px-1">
              <div className="flex items-center gap-2 flex-1 min-w-0 py-1.5 px-1">
                <div className="w-8 h-8 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                  {initials}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-700 truncate">
                    {user?.first_name || user?.email || 'Utilisateur'}
                  </div>
                </div>
              </div>
              <button
                onClick={handleLogout}
                title="Se deconnecter"
                className="p-1.5 rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors flex-shrink-0"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
                </svg>
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Contenu principal */}
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  )
}
